import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  Typography,
} from "@material-ui/core";
import {
  ApplicationConfig,
  ConfigurableListOption,
  ConfigurableRoleOption,
} from "resource-planner-model";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AppState,
  ReportFeedbackAction,
  REPORT_APP_FEEDBACK,
} from "../redux/AppTypes";
import {
  FETCH_APPLICATION_CONFIG,
  PERSIST_APPLICATION_CONFIG,
} from "./ApplicationConfigTypes";

type EditableConfigOption = ConfigurableListOption & Partial<ConfigurableRoleOption>;

function parseDomains(value: string): string[] {
  return Array.from(
    new Set(
      value
        .split(/[\n,]/)
        .map((domain) => domain.trim().toLowerCase())
        .filter(Boolean)
    )
  );
}

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function orderOptions(
  options: EditableConfigOption[]
): EditableConfigOption[] {
  return options.map((option, order) => ({ ...option, order }));
}

function addOption(
  options: EditableConfigOption[],
  label: string
): EditableConfigOption[] {
  let id = slugify(label);
  let suffix = 2;
  const existing = new Set(options.map((item) => item.id));
  while (existing.has(id)) {
    id = `${slugify(label)}-${suffix}`;
    suffix += 1;
  }
  return [
    ...options,
    {
      id,
      label,
      archived: false,
      order: options.length,
    },
  ];
}

function ConfigurableListEditor({
  title,
  addLabel,
  options,
  onChange,
  behaviorLabel,
  behaviorKey,
}: {
  title: string;
  addLabel: string;
  options: EditableConfigOption[];
  onChange: (options: EditableConfigOption[]) => void;
  behaviorLabel?: string;
  behaviorKey?: keyof ConfigurableRoleOption;
}) {
  function updateOption(
    index: number,
    patch: Partial<EditableConfigOption>
  ): void {
    onChange(
      options.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...patch } : item
      )
    );
  }

  function moveOption(index: number, direction: -1 | 1): void {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= options.length) {
      return;
    }
    const next = [...options];
    [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
    onChange(orderOptions(next));
  }

  return (
    <Box marginTop={4}>
      <Typography variant="h5">{title}</Typography>
      <Box marginTop={1} display="flex" flexDirection="column">
        {options.map((option, index) => (
          <Box
            key={option.id}
            display="flex"
            alignItems="center"
            marginBottom={1}
          >
            <TextField
              label="Label"
              value={option.label}
              onChange={(event) =>
                updateOption(index, { label: event.target.value })
              }
              variant="outlined"
              size="small"
              style={{ flexGrow: 1 }}
            />
            <Box marginLeft={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={option.archived}
                    onChange={(event) =>
                      updateOption(index, {
                        archived: event.target.checked,
                      })
                    }
                    color="primary"
                  />
                }
                label="Archived"
              />
            </Box>
            {behaviorKey && behaviorLabel ? (
              <Box marginLeft={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!option[behaviorKey]}
                      onChange={(event) =>
                        updateOption(index, {
                          [behaviorKey]: event.target.checked,
                        } as Partial<ConfigurableListOption>)
                      }
                      color="primary"
                    />
                  }
                  label={behaviorLabel}
                />
              </Box>
            ) : null}
            <Box marginLeft={1}>
              <Button
                onClick={() => moveOption(index, -1)}
                disabled={index === 0}
              >
                Up
              </Button>
              <Button
                onClick={() => moveOption(index, 1)}
                disabled={index === options.length - 1}
              >
                Down
              </Button>
            </Box>
          </Box>
        ))}
        <Box>
          <Button
            variant="outlined"
            onClick={() => onChange(addOption(options, addLabel))}
          >
            Add {title.toLowerCase().slice(0, -1)}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export function ApplicationConfigPage() {
  const dispatch = useDispatch();
  const config = useSelector((state: AppState) => state.applicationConfig.item);
  const [domains, setDomains] = useState("");
  const [cloudTypes, setCloudTypes] = useState<ConfigurableListOption[]>([]);
  const [engineerRoles, setEngineerRoles] = useState<ConfigurableRoleOption[]>([]);
  const [productTypes, setProductTypes] = useState<ConfigurableListOption[]>([]);
  const [projectManagerRoles, setProjectManagerRoles] = useState<
    ConfigurableRoleOption[]
  >([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const response = dispatch({ type: FETCH_APPLICATION_CONFIG }) as any;
    Promise.resolve(response).finally(() => setLoading(false));
  }, [dispatch]);

  useEffect(() => {
    setDomains((config.corporateEmailDomains || []).join("\n"));
    setCloudTypes(
      orderOptions(config.cloudTypes || []) as ConfigurableListOption[]
    );
    setEngineerRoles(
      orderOptions(config.engineerRoles || []) as ConfigurableRoleOption[]
    );
    setProductTypes(
      orderOptions(config.productTypes || []) as ConfigurableListOption[]
    );
    setProjectManagerRoles(
      orderOptions(config.projectManagerRoles || []) as ConfigurableRoleOption[]
    );
  }, [config]);

  function save(): void {
    const item: ApplicationConfig = {
      id: config.id,
      corporateEmailDomains: parseDomains(domains),
      cloudTypes: orderOptions(cloudTypes),
      engineerRoles: orderOptions(engineerRoles) as ApplicationConfig["engineerRoles"],
      productTypes: orderOptions(productTypes),
      projectManagerRoles: orderOptions(
        projectManagerRoles
      ) as ApplicationConfig["projectManagerRoles"],
      plugins: config.plugins,
    };
    const response = dispatch({
      type: PERSIST_APPLICATION_CONFIG,
      item,
    }) as any;
    response.catch((error: any) => {
      const action: ReportFeedbackAction = {
        type: REPORT_APP_FEEDBACK,
        feedback: error.message || "error",
      };
      dispatch(action);
    });
  }

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Box maxWidth={860}>
      <Typography variant="h4">Application settings</Typography>
      <Box marginTop={2} display="flex" flexDirection="column">
        <TextField
          label="Corporate email domains"
          value={domains}
          onChange={(event) => setDomains(event.target.value)}
          multiline
          minRows={6}
          variant="outlined"
          helperText="One domain per line. These domains control corporate email matching for access, Jira, and Microsoft Shifts workflows."
        />
        <ConfigurableListEditor
          title="Cloud types"
          addLabel="New cloud type"
          options={cloudTypes}
          onChange={(options) =>
            setCloudTypes(options as ConfigurableListOption[])
          }
        />
        <ConfigurableListEditor
          title="Engineer roles"
          addLabel="New engineer role"
          options={engineerRoles}
          onChange={(options) =>
            setEngineerRoles(options as ConfigurableRoleOption[])
          }
          behaviorLabel="Grants access"
          behaviorKey="grantsEngineerLeadAccess"
        />
        <ConfigurableListEditor
          title="Product types"
          addLabel="New product type"
          options={productTypes}
          onChange={(options) =>
            setProductTypes(options as ConfigurableListOption[])
          }
        />
        <ConfigurableListEditor
          title="Project Manager roles"
          addLabel="New Project Manager role"
          options={projectManagerRoles}
          onChange={(options) =>
            setProjectManagerRoles(options as ConfigurableRoleOption[])
          }
          behaviorLabel="Counts as lead"
          behaviorKey="countsAsProjectManagerLead"
        />
        <Box marginTop={2}>
          <Button variant="contained" color="primary" onClick={save}>
            Save settings
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
